import { Injectable } from "@angular/core";
import { PointerTracker } from "../primitives/pointer-tracker";
import { InputMiddleware } from "../primitives/i-input-middlware";
import {PointerResult} from "../primitives/i-pointer-result";
import {IInputTuning} from "../input-tunings/i-input-tuning";
import {WheelEventState, WheelGestures} from 'wheel-gestures'
import {Vector2} from "three";


@Injectable({
  providedIn: "root",
})
export class InputService {
  pointerTrackers: Map<string, PointerTracker> = new Map();
  middlewareIdByTrackerId: Map<string, string> = new Map();
  inputMiddlewares: Map<string, InputMiddleware> = new Map();
  element: HTMLCanvasElement;

  ctrlPressed = false
  debug = false;

  lastCtrlEvent: number

  wheelGestures: any

  inputTunings: Map<string, IInputTuning> = new Map();

  lastPointerPosition: Vector2 = new Vector2();

  constructor() {}

  async makeHtmlElementTouchable(element: HTMLCanvasElement) {
    element.onpointerdown = this.touchDown.bind(this);
    element.onpointermove = this.touchMove.bind(this);
    element.onpointerup = this.touchUp.bind(this);
    element.onwheel = this.scroll.bind(this);
    element.oncontextmenu = this.contextMenu.bind(this);
    element.style.touchAction = "none";
    this.wheelGestures = WheelGestures()
    this.wheelGestures.observe(element)
    this.element = element;

      
    // this.wheelGestures.on('wheel', this.scroll.bind(this))
    const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
  }

  getElementPosition(element) {
    let x = 0,
      y = 0;
    while (element) {
      x += element.offsetLeft;
      y += element.offsetTop;
      element = element.offsetParent; // Move up to the next offset parent
    }
    return { top: y, left: x };
  }

  async addInputMiddleware(id: string, middleware: InputMiddleware) {
    await middleware.init();
    this.inputMiddlewares.set(id, middleware);
  }

  logPointerResult(event: string, result: PointerResult) {
    if (!this.debug) return;
    const child = this.getChildThatConsumedEvent(result);
    console.log("[InputService] " + event + " consumed by " + child.id);
    // console.log(result);
  }

  log(message) {
    if (!this.debug) return;
    console.log("[InputService] " + message);
  }

  getLastPointerPosition() {
    return this.lastPointerPosition;
  }

  getChildThatConsumedEvent(result: PointerResult) {
    if (result.consumedByChild) {
      for (let child of result.children) {
        if (child.consumed) {
          if (!child.consumedByChild) {
            return child
          }
          return this.getChildThatConsumedEvent(child);
        }
      }
      return null
    } else {
      return result;
    }
  }

  touchDown(event: PointerEvent) {
    event.preventDefault();
    (document.activeElement as HTMLElement)?.blur();
    this.element.focus();
    const elementRect = this.getElementPosition(this.element);
    const pointTracker = new PointerTracker(
      event.pageX - elementRect.left,
      event.pageY - elementRect.top,
      event,
    );
    this.lastPointerPosition.set(pointTracker.pos.x, pointTracker.pos.y);
    this.pointerTrackers.set(`${event.pointerId}`, pointTracker);
    for (let key of Array.from(this.inputMiddlewares.keys())) {
      const middleware = this.inputMiddlewares.get(key);
      const ptResult = middleware.newEvent({
        eventType: "pointerAdded",
        pointer: pointTracker,
        pointerOld: null
      });
      if (ptResult.consumed) {
        this.middlewareIdByTrackerId.set(pointTracker.id, key);
        this.logPointerResult("pointerAdded", ptResult);
        return;
      }
    }
  }

  touchMove(event: PointerEvent) {
    event.preventDefault();
    const elementRect = this.getElementPosition(this.element);
    const newEvent = new PointerTracker(
      event.pageX - elementRect.left,
      event.pageY - elementRect.top,
      event,
    );
    this.lastPointerPosition.set(newEvent.pos.x, newEvent.pos.y);
    let oldEvent = null;
    if (this.pointerTrackers.has(`${event.pointerId}`)) {
      oldEvent = this.pointerTrackers.get(`${event.pointerId}`);
    }
    let middlewareKeys = Array.from(this.inputMiddlewares.keys());
    if (this.middlewareIdByTrackerId.has(newEvent.id)) {
      middlewareKeys = [this.middlewareIdByTrackerId.get(newEvent.id)];
    }
    let cursorToPointer = false;
    for (let key of middlewareKeys) {
      const middleware = this.inputMiddlewares.get(key);
      // await middleware.pointerChanged(oldEvent, newEvent);
      const ptResult = middleware.newEvent({
        eventType: "pointerChanged",
        pointer: newEvent,
        pointerOld: oldEvent
      });
      // if (ptResult.consumed)
       this.logPointerResult("pointerChanged", ptResult);
       if (ptResult.cursorToPointer) {
         cursorToPointer = true;
       }
    }
    this.log("cursorToPointer: " +  cursorToPointer);
    this.cursorPointer(cursorToPointer);
  }

  cursorPointer(enabled: boolean) {
    if (enabled) {
      this.element.style.cursor = "pointer";
    } else {
      this.element.style.cursor = "default";
    }
  }

  touchUp(event: PointerEvent) {
    event.preventDefault();
    const elementRect = this.getElementPosition(this.element);
    const newEvent = new PointerTracker(
      event.pageX - elementRect.left,
      event.pageY - elementRect.top,
      event,
    );
    let middlewareKeys = Array.from(this.inputMiddlewares.keys());
    let sunk = false;
    for (let key of middlewareKeys) {
      const middleware = this.inputMiddlewares.get(key);
      // let result = await middleware.pointerRemoved(newEvent, sunk);
      const ptResult = middleware.newEvent({
        eventType: "pointerRemoved",
        pointer: newEvent,
      });
      if (ptResult.consumed) this.logPointerResult("pointerRemoved", ptResult);
      sunk = sunk || ptResult.consumed;
    }
    this.pointerTrackers.delete(`${event.pointerId}`);
    this.middlewareIdByTrackerId.delete(event.pointerId + "");
  }

  contextMenu(event: PointerEvent) {
    event.preventDefault();
    const elementRect = this.getElementPosition(this.element);
    const pointTracker = new PointerTracker(
        event.pageX - elementRect.left,
        event.pageY - elementRect.top,
        event,
    );
    for (let key of Array.from(this.inputMiddlewares.keys())) {
      const middleware = this.inputMiddlewares.get(key);
      const sunk = middleware.contextMenu(pointTracker);
    }
  }

  scroll(event: WheelEvent) {
    event.preventDefault();
    for (let key of Array.from(this.inputMiddlewares.keys())) {
      const middleware = this.inputMiddlewares.get(key);
      event["isCtrlDeliberatelyPressed"] = this.ctrlPressed
      const sunk = middleware.scroll(event as WheelEvent);
    }
    return true;
  }
}
